import { useState } from "react"
import { router } from "@inertiajs/react"
import { Dialog, DialogContent, DialogBody, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Loader2 } from "lucide-react"
import type { Project } from "@/types"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectA: Project
  projectB: Project
  onSuccess: () => void
}

function formatAddress(p: Project) {
  return [p.address, [p.zip_code, p.city_name].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"
}

export function MergeProjectsDialog({ open, onOpenChange, projectA, projectB, onSuccess }: Props) {
  const [nameChoice, setNameChoice] = useState<"first" | "second">("first")
  const [addressChoice, setAddressChoice] = useState<"first" | "second">("first")
  const [checklistChoice, setChecklistChoice] = useState<"first" | "second">("first")
  const [ownerFirst, setOwnerFirst] = useState(true)
  const [ownerSecond, setOwnerSecond] = useState(true)
  const [processing, setProcessing] = useState(false)

  const canSubmit = ownerFirst || ownerSecond

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const owners: string[] = []
    if (ownerFirst) owners.push("first")
    if (ownerSecond) owners.push("second")

    setProcessing(true)
    router.post(
      "/projects/merge",
      {
        first_project_id: projectA.id,
        second_project_id: projectB.id,
        choices: {
          name: nameChoice,
          address: addressChoice,
          checklist_items: checklistChoice,
          owners,
        },
      },
      {
        onSuccess: () => {
          onSuccess()
          onOpenChange(false)
        },
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Fusionner deux projets</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <DialogBody className="space-y-5">
            {/* --- Projets sélectionnés --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Projets sélectionnés</h4>
              <p className="text-sm text-muted-foreground">
                Le premier projet sera conservé avec les données fusionnées. Le second sera supprimé.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-lg border p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Projet 1 (conservé)</p>
                  <p className="font-medium truncate">{projectA.name}</p>
                  <p className="text-muted-foreground text-sm truncate">{formatAddress(projectA)}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Projet 2 (supprimé)</p>
                  <p className="font-medium truncate">{projectB.name}</p>
                  <p className="text-muted-foreground text-sm truncate">{formatAddress(projectB)}</p>
                </div>
              </div>
            </div>

            {/* --- Champs à choisir --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Champs à choisir</h4>

              {/* Name */}
              <div className="space-y-2">
                <Label>Nom</Label>
                <RadioGroup value={nameChoice} onValueChange={(v) => setNameChoice(v as "first" | "second")}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Label htmlFor="name-first" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="first" id="name-first" />
                      <span className="text-sm truncate">{projectA.name}</span>
                    </Label>
                    <Label htmlFor="name-second" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="second" id="name-second" />
                      <span className="text-sm truncate">{projectB.name}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label>Adresse</Label>
                <RadioGroup value={addressChoice} onValueChange={(v) => setAddressChoice(v as "first" | "second")}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Label htmlFor="address-first" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="first" id="address-first" />
                      <span className="text-sm truncate">{formatAddress(projectA)}</span>
                    </Label>
                    <Label htmlFor="address-second" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="second" id="address-second" />
                      <span className="text-sm truncate">{formatAddress(projectB)}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Checklist */}
              <div className="space-y-2">
                <Label>Check-list</Label>
                <RadioGroup value={checklistChoice} onValueChange={(v) => setChecklistChoice(v as "first" | "second")}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Label htmlFor="checklist-first" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="first" id="checklist-first" />
                      <span className="text-sm">Projet 1</span>
                    </Label>
                    <Label htmlFor="checklist-second" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                      <RadioGroupItem value="second" id="checklist-second" />
                      <span className="text-sm">Projet 2</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Owners */}
              <div className="space-y-2">
                <Label>Propriétaires</Label>
                <div className="grid gap-4 md:grid-cols-2">
                  <Label htmlFor="owner-first" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                    <Checkbox
                      id="owner-first"
                      checked={ownerFirst}
                      onCheckedChange={(v) => {
                        if (!v && !ownerSecond) return
                        setOwnerFirst(!!v)
                      }}
                    />
                    <span className="text-sm">Projet 1</span>
                  </Label>
                  <Label htmlFor="owner-second" className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-accent has-[[data-state=checked]]:border-primary">
                    <Checkbox
                      id="owner-second"
                      checked={ownerSecond}
                      onCheckedChange={(v) => {
                        if (!v && !ownerFirst) return
                        setOwnerSecond(!!v)
                      }}
                    />
                    <span className="text-sm">Projet 2</span>
                  </Label>
                </div>
              </div>
            </div>

            {/* --- Fusion automatique --- */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Fusion automatique</h4>
              <p className="text-sm text-muted-foreground">
                Les parcelles, notes, événements, tâches, offres, utilisateurs assignés, équipes assignées et tags seront automatiquement fusionnés.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
              Annuler
            </Button>
            <Button type="submit" disabled={!canSubmit || processing}>
              {processing && <Loader2 className="size-4 animate-spin" />}
              Fusionner
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
